import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Quote } from '../../types/quote';
import { QuoteJobInfo } from '../quotes/QuoteJobInfo';
import { getQuoteStatus } from '../../utils/quoteUtils';
import { ReviewForm } from './ReviewForm';
import { useReviewStore } from '../../stores/reviewStore';
import { Button } from '../common/Button';
import { Review } from '../../types/review';

interface ReviewModalProps {
  quote: Quote;
  onComplete?: () => void;
  onClose: () => void;
}

const FreelancerReview: React.FC<ReviewModalProps> = ({ quote, onComplete, onClose }) => {
  const { t } = useTranslation(['reviews', 'common']);
  const [sending, setSending] = useState(false);

  const { submitReview } = useReviewStore();

  const [formData, setFormData] = useState<null | Review>(null);

  const handleSend = async () => {
    if (!formData) return;
    try {
      setSending(true);
      await submitReview(formData);
      onComplete?.();
    } catch (err) {
      console.error('Erreur envoi review:', err);
    } finally {
      setSending(false);
    }
  };

  const projectMode = true;
  const quoteStatus = getQuoteStatus(quote);

  const companyName = quote.job.client.company_name;
  const recruiterName = `${quote.job.client.first_name} ${quote.job.client.last_name}`;

  const quoteStatusLabel = projectMode
    ? t(`job_statuses.${quote.job.status}`, { ns: 'common' })
    : t(`quote_statuses.${quoteStatus}`, { ns: 'common' });

  return (
    <>
      <h2 className="text-2xl md:text-3xl font-bold text-[#304659] mb-4 text-left">
        {t('reviewModalTitle', { ns: 'reviews', defaultValue: 'Évaluation du projet' })}
      </h2>

      <QuoteJobInfo quoteJob={quote.job} headerOnly={true} statusLabel={quoteStatusLabel} />

      <hr className="border-[#E1E9F0]" />

      {/* Recruiter review */}
      <div>
        <p className="font-bold text-[#304659] my-2">
          {t('yourRecruiter', { ns: 'reviews', defaultValue: 'Votre recruteur' })}
        </p>
        <h2 className="font-bold text-2xl text-[#304659]">{companyName}</h2>
        <p className="text-xs">
          {t('responsible', { ns: 'reviews', defaultValue: 'Responsable' })}
        </p>
        <p className="text-[#304659] font-bold">{recruiterName}</p>

        <div className="mt-6">
          <h3 className="text-lg font-bold text-[#304659] mb-2">
            {t('review', { ns: 'reviews', defaultValue: 'Évaluation' })}
          </h3>
          <ReviewForm
            quoteId={quote.id}
            reviewedId={quote.job.client.id}
            username={recruiterName}
            onChange={setFormData}
          />
        </div>
      </div>

      <div className="flex justify-end space-x-2 mt-6">
        <Button
          type="button"
          onClick={onClose}
          className="w-full md:w-auto md:px-8"
          variant="simple-text"
        >
          {t('common:form_actions.cancel_close', { ns: 'common' })}
        </Button>

        <Button
          className="w-full md:w-auto md:px-8"
          variant="primary"
          type="button"
          onClick={handleSend}
          disabled={sending}
        >
          {sending
            ? t('common:form_actions.sending', { ns: 'common', defaultValue: 'Envoi...' })
            : t('common:form_actions.send', { ns: 'common', defaultValue: 'Envoyer' })}
        </Button>
      </div>
    </>
  );
};

export default FreelancerReview;
